import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import type {ProjectRosterRowResponse} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {formatUtcShortDate} from 'src/shared/utils/formatters';

export const useProjectRosterColumns = () => {
  const columns = useMemo<MRT_ColumnDef<ProjectRosterRowResponse>[]>(
    () => [
      {
        accessorKey: 'userName',
        header: 'Person',
        Cell: ({cell}) => (
          <Text fw={500} size="sm">
            {cell.getValue<string | undefined>() ?? '—'}
          </Text>
        ),
      },
      {
        accessorKey: 'departmentName',
        header: 'Department',
        Cell: ({cell}) => cell.getValue<string | undefined>() ?? '—',
      },
      {
        accessorKey: 'vendorName',
        header: 'Vendor',
        Cell: ({cell}) => cell.getValue<string | undefined>() ?? '—',
      },
      {
        accessorKey: 'rules',
        header: 'Assignment Rules',
        // The server rejects filter/sort on the rules relation.
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({row}) => {
          const rules = row.original.rules;
          if (rules.length === 0) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }
          return (
            <Group gap="xs">
              {rules.map(rule => (
                <Pill
                  key={rule.ruleId}
                  text={rule.name ?? `Rule #${rule.ruleId}`}
                  color="blue"
                />
              ))}
            </Group>
          );
        },
      },
      {
        accessorKey: 'assignedAt',
        header: 'Assigned',
        Cell: ({cell}) => formatUtcShortDate(cell.getValue<Date>()),
      },
    ],
    []
  );

  return columns;
};
